import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ScanFace, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
interface CameraStepProps {
  onCapture: (imageSrc: string) => void;
}
const FACE_API_SCRIPT = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.js';
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
// Singleton script-load promise
let faceApiScriptPromise: Promise<any> | null = null;
function loadFaceApiScript(): Promise<any> {
  if ((window as any).faceapi) return Promise.resolve((window as any).faceapi);
  if (faceApiScriptPromise) return faceApiScriptPromise;
  faceApiScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = FACE_API_SCRIPT;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      const fa = (window as any).faceapi;
      if (fa) resolve(fa);else {
        faceApiScriptPromise = null;
        reject(new Error('face-api global not found'));
      }
    };
    script.onerror = () => {
      faceApiScriptPromise = null;
      reject(new Error('Failed to load face-api.js script'));
    };
    document.head.appendChild(script);
  });
  return faceApiScriptPromise;
}
export function CameraStep({
  onCapture
}: CameraStepProps) {
  const {
    t
  } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionIntervalRef = useRef<number | null>(null);
  const faceapiRef = useRef<any>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [detectionStatus, setDetectionStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [faceDetected, setFaceDetected] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  // Load face detection script + model
  useEffect(() => {
    let cancelled = false;
    setDetectionStatus('loading');
    const init = async () => {
      try {
        const faceapi = await loadFaceApiScript();
        if (cancelled) return;
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        if (cancelled) return;
        faceapiRef.current = faceapi;
        setDetectionStatus('ready');
      } catch (err) {
        console.error('Face detection unavailable:', err);
        if (!cancelled) setDetectionStatus('error');
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [retryToken]);
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setHasPermission(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setHasPermission(false);
    }
  }, []);
  useEffect(() => {
    startCamera();
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
      if (detectionIntervalRef.current) {
        window.clearInterval(detectionIntervalRef.current);
      }
    };
  }, [startCamera]);
  // Run face detection once detection is ready and camera is on
  useEffect(() => {
    if (detectionStatus !== 'ready' || !hasPermission || !faceapiRef.current) return;
    const faceapi = faceapiRef.current;
    const detect = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) return;
      try {
        // Smaller input size on mobile for faster detection on lower-end devices
        const isMobile = window.innerWidth < 640;
        const inputSize = isMobile ? 224 : 320;
        const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({
          inputSize,
          scoreThreshold: 0.5
        }));
        setFaceDetected(!!detection && detection.score >= 0.5);
      } catch (err) {
        // ignore intermittent detection errors
      }
    };
    // Slower interval on mobile to reduce CPU/battery load
    const interval = window.innerWidth < 640 ? 600 : 400;
    detectionIntervalRef.current = window.setInterval(detect, interval);
    return () => {
      if (detectionIntervalRef.current) {
        window.clearInterval(detectionIntervalRef.current);
      }
    };
  }, [detectionStatus, hasPermission]);
  const handleCaptureClick = () => {
    // Strict: must have face detected. No fallback bypass.
    if (detectionStatus !== 'ready' || !faceDetected) {
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 2500);
      return;
    }
    setCountdown(3);
    let count = 3;
    const timer = setInterval(() => {
      count -= 1;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(timer);
        setCountdown(null);
        executeCapture();
      }
    }, 1000);
  };
  const executeCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageSrc = canvas.toDataURL('image/jpeg', 0.9);
        const stream = video.srcObject as MediaStream;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        onCapture(imageSrc);
      }
    }
  };
  // Camera permission denied state
  if (hasPermission === false) {
    return <div className="bg-white p-8 rounded-2xl text-center max-w-md mx-auto shadow-xl" data-id="element-206">
        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-207">
          <Camera className="w-8 h-8" data-id="element-208" />
        </div>
        <h3 className="text-xl font-semibold mb-2" data-id="element-209">Camera Access Denied</h3>
        <p className="text-gray-600 mb-6" data-id="element-210">
          {t('faceAnalysis.error.cameraDenied')}
        </p>
        <button onClick={startCamera} className="bg-brand-copper text-white px-6 py-2 rounded-full font-medium hover:bg-brand-darkCopper transition-colors" data-id="element-211">
          {t('faceAnalysis.error.tryAgain')}
        </button>
      </div>;
  }
  // Detection model failed to load
  if (detectionStatus === 'error') {
    return <div className="bg-white p-8 rounded-2xl text-center max-w-md mx-auto shadow-xl" data-id="element-212">
        <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-213">
          <AlertTriangle className="w-8 h-8" data-id="element-214" />
        </div>
        <h3 className="text-xl font-semibold mb-2" data-id="element-215">Detection Unavailable</h3>
        <p className="text-gray-600 mb-6" data-id="element-216">
          Could not load the face detection model. Please check your internet
          connection and try again.
        </p>
        <button onClick={() => setRetryToken(n => n + 1)} className="bg-brand-copper text-white px-6 py-2 rounded-full font-medium hover:bg-brand-darkCopper transition-colors inline-flex items-center" data-id="element-217">
          <RefreshCw className="w-4 h-4 mr-2" data-id="element-218" />
          {t('faceAnalysis.error.tryAgain')}
        </button>
      </div>;
  }
  // Status indicator
  const getStatus = () => {
    if (detectionStatus === 'loading') {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" data-id="element-219" />,
        text: t('faceAnalysis.camera.loadingModel'),
        bg: 'bg-gray-700/80'
      };
    }
    if (faceDetected) {
      return {
        icon: <CheckCircle2 className="w-4 h-4" data-id="element-220" />,
        text: t('faceAnalysis.camera.faceDetected'),
        bg: 'bg-green-500/90'
      };
    }
    return {
      icon: <AlertTriangle className="w-4 h-4" data-id="element-221" />,
      text: t('faceAnalysis.camera.noFace'),
      bg: 'bg-amber-500/90'
    };
  };
  const status = getStatus();
  const captureEnabled = detectionStatus === 'ready' && faceDetected;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="max-w-2xl mx-auto bg-gray-900 rounded-3xl overflow-hidden shadow-2xl relative" data-id="element-222">
      {/* Header — hidden on small screens (status pill conveys instruction) */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 z-20 hidden sm:flex justify-between items-center bg-gradient-to-b from-black/60 to-transparent" data-id="element-223">
        <p className="text-white font-medium flex items-center text-sm sm:text-base" data-id="element-224">
          <ScanFace className="w-5 h-5 mr-2 text-brand-copper" data-id="element-225" />
          {t('faceAnalysis.camera.instruction')}
        </p>
      </div>

      {/* Video Container — taller on mobile for portrait selfie framing */}
      <div className="relative h-[65vh] min-h-[420px] sm:h-auto sm:aspect-video bg-black" data-id="element-226">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{
        transform: 'scaleX(-1)'
      }} data-id="element-227" />
        <canvas ref={canvasRef} className="hidden" data-id="element-228" />

        {/* Animated Overlay - Scanning Frame */}
        <div className={`absolute inset-3 sm:inset-8 border-2 rounded-2xl z-10 pointer-events-none transition-colors duration-300 ${faceDetected ? 'border-green-400/50' : 'border-brand-copper/30'}`} data-id="element-229">
          <div className={`absolute top-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-t-[3px] border-l-[3px] sm:border-t-4 sm:border-l-4 rounded-tl-xl transition-colors ${faceDetected ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-230"></div>
          <div className={`absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-t-[3px] border-r-[3px] sm:border-t-4 sm:border-r-4 rounded-tr-xl transition-colors ${faceDetected ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-231"></div>
          <div className={`absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-b-[3px] border-l-[3px] sm:border-b-4 sm:border-l-4 rounded-bl-xl transition-colors ${faceDetected ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-232"></div>
          <div className={`absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-b-[3px] border-r-[3px] sm:border-b-4 sm:border-r-4 rounded-br-xl transition-colors ${faceDetected ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-233"></div>

          {!faceDetected && <motion.div animate={{
          top: ['0%', '100%', '0%']
        }} transition={{
          duration: 3,
          ease: 'linear',
          repeat: Infinity
        }} className="absolute left-0 right-0 h-0.5 bg-brand-copper/50 shadow-[0_0_15px_rgba(199,146,104,0.8)] z-10" data-id="element-234" />}
        </div>

        {/* Status Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20" data-id="element-235">
          <motion.div key={status.text} initial={{
          y: 10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} className={`${status.bg} text-white backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium flex items-center shadow-lg max-w-[90vw]`} data-id="element-236">
            <span className="mr-2 flex-shrink-0" data-id="element-237">{status.icon}</span>
            <span className="truncate" data-id="element-238">{status.text}</span>
          </motion.div>
        </div>

        {/* Warning Toast */}
        <AnimatePresence data-id="element-239">
          {showWarning && <motion.div initial={{
          y: -20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -20,
          opacity: 0
        }} className="absolute top-20 left-1/2 -translate-x-1/2 z-30 bg-red-500 text-white px-5 py-3 rounded-xl shadow-xl flex items-center max-w-[90%]" data-id="element-240">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-241" />
              <span className="text-sm font-medium" data-id="element-242">
                {t('faceAnalysis.camera.notHuman')}
              </span>
            </motion.div>}
        </AnimatePresence>

        {/* Countdown Overlay */}
        {countdown !== null && <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/40 backdrop-blur-sm" data-id="element-243">
            <motion.div key={countdown} initial={{
          scale: 2,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.5,
          opacity: 0
        }} className="text-7xl font-bold text-white drop-shadow-lg" data-id="element-244">
              {countdown}
            </motion.div>
          </div>}
      </div>

      {/* Controls */}
      <div className="p-6 bg-gray-900 flex justify-center items-center" data-id="element-245">
        <button onClick={handleCaptureClick} disabled={countdown !== null} className={`w-20 h-20 rounded-full border-4 flex items-center justify-center transition-all group ${captureEnabled ? 'border-green-400/50 hover:border-green-400' : 'border-white/20 cursor-not-allowed opacity-60'}`} data-id="element-246">
          <div className={`w-16 h-16 rounded-full transition-colors flex items-center justify-center ${captureEnabled ? 'bg-white group-hover:bg-green-400' : 'bg-gray-400'}`} data-id="element-247">
            <Camera className={`w-8 h-8 transition-colors ${captureEnabled ? 'text-gray-900 group-hover:text-white' : 'text-gray-600'}`} data-id="element-248" />
          </div>
        </button>
      </div>
    </motion.div>;
}